import { defineStore } from "pinia";
import ApiConnection from "@/api";
import type {
  ActivityGroup,
  ActivityGroupNewData,
  ActivityGroupUpdate,
} from "@/types/activityGroup";

import { mergeEventPayload } from "@/mixins/dataHelper";
import { createCoalescer } from "@/mixins/requestCoalescer";
import { eventRouter } from "@/api/eventRouter";
import type { NormalizedEvent } from "@/api/eventRouter";
import { appStateStore } from "@/stores/appState";

const API = ApiConnection.activityGroups;

/** Coalesces WS-event-triggered reloads (ws-event-handling-rework.md §3.2). */
const coalesce = createCoalescer();

export const activityGroupsStore = defineStore({
  id: "activityGroups",
  state: () => ({
    inited: false,
    activityGroups: [] as ActivityGroup[],
    activityGroupsByPage: {
      activityGroups: [] as ActivityGroup[],
      limit: 20,
      page: 1,
    },
  }),

  actions: {
    init() {
      if (this.$state.inited) {
        return;
      }
      this.$state.inited = true;
      eventRouter.route(
        "activity_group_change",
        undefined,
        (e) => {
          if (e.eventType === "change" && e.entityId && e.newState) {
            this.applyChange(e.entityId, e.newState);
          } else if (e.eventType === "change" && e.entityId) {
            this.reloadupdateActivityGroupData(e.entityId);
          } else if (e.eventType === "new" && e.entityId && e.newState) {
            this.applyNew(e.entityId, e.newState as ActivityGroup);
          } else if (e.eventType === "delete" && e.entityId) {
            this.applyDelete(e.entityId);
          } else {
            coalesce("activityGroups:full", () =>
              Promise.all([
                this.getAll(),
                this.getActivityGroupsByPageByLimit(
                  this.activityGroupsByPage.page,
                  this.activityGroupsByPage.limit
                ),
              ])
            );
          }
          this.socketUpdate(e.msgData, e);
        },
        { name: "activityGroups" }
      );
      appStateStore().registerResyncHandler("activityGroups", () => {
        if (this.$state.activityGroups.length > 0) {
          coalesce("activityGroups:full", () => this.getAll());
        }
        if (this.$state.activityGroupsByPage.activityGroups.length > 0) {
          coalesce("activityGroups:page", () =>
            this.getActivityGroupsByPageByLimit(
              this.activityGroupsByPage.page,
              this.activityGroupsByPage.limit
            )
          );
        }
      });
    },

    async getAll(): Promise<ActivityGroup[]> {
      this.init();
      try {
        const items = await API.getAll();
        this.$state.activityGroups = items ?? [];
        return items;
      } catch (e) {
        return [];
      }
    },

    async getActivityGroupsByPageByLimit(
      page = 1,
      limit = 100
    ): Promise<{
      data: { activityGroups: ActivityGroup[]; limit: number; page: number };
      headers: object;
    }> {
      this.init();
      let newActivityGroups = <{ data: ActivityGroup[]; headers?: object }>{};
      newActivityGroups = await API.getActivityGroupsByPageByLimit(page, limit);
      this.$state.activityGroupsByPage.activityGroups =
        newActivityGroups.data ?? [];
      this.$state.activityGroupsByPage.limit = limit;
      this.$state.activityGroupsByPage.page = page;
      return {
        data: this.$state.activityGroupsByPage,
        headers: newActivityGroups.headers || {},
      };
    },

    async getActivityGroup(groupId: string): Promise<ActivityGroup> {
      this.init();
      return await API.getActivityGroup(groupId);
    },

    /** Store contract (ws-event-handling-rework.md §4.2): in-place merge,
     *  cache miss updates nothing. */
    applyChange(groupId: string, patch: Record<string, any>) {
      if (patch.group_id && patch.group_id !== groupId) {
        return;
      }
      const inFull = this.$state.activityGroups.find(
        (g) => g.group_id === groupId
      );
      if (inFull) {
        mergeEventPayload(inFull, patch);
      }
      const inPage = this.$state.activityGroupsByPage.activityGroups.find(
        (g) => g.group_id === groupId
      );
      if (inPage) {
        mergeEventPayload(inPage, patch);
      }
    },

    /** NEW events carry the complete entity (core guarantee, task doc OQ-2). */
    applyNew(groupId: string, group: ActivityGroup) {
      if (!this.$state.activityGroups.some((g) => g.group_id === groupId)) {
        this.$state.activityGroups.push(group);
      }
      coalesce("activityGroups:page", () =>
        this.getActivityGroupsByPageByLimit(
          this.activityGroupsByPage.page,
          this.activityGroupsByPage.limit
        )
      );
    },

    /** Targeted removal — no full reloads on delete (task doc P1-3). */
    applyDelete(groupId: string) {
      const fullIndex = this.$state.activityGroups.findIndex(
        (g) => g.group_id === groupId
      );
      if (fullIndex > -1) {
        this.$state.activityGroups.splice(fullIndex, 1);
      }
      const pageIndex =
        this.$state.activityGroupsByPage.activityGroups.findIndex(
          (g) => g.group_id === groupId
        );
      if (pageIndex > -1) {
        this.$state.activityGroupsByPage.activityGroups.splice(pageIndex, 1);
      }
      if (
        this.$state.activityGroupsByPage.activityGroups.length > 0 ||
        pageIndex > -1
      ) {
        coalesce("activityGroups:page", () =>
          this.getActivityGroupsByPageByLimit(
            this.activityGroupsByPage.page,
            this.activityGroupsByPage.limit
          )
        );
      }
    },

    /** Change event without payload: reload the single entity via REST. */
    async reloadupdateActivityGroupData(group_id: string) {
      const updatedActivityGroup = await API.getActivityGroup(group_id);
      this.applyChange(updatedActivityGroup.group_id, updatedActivityGroup);
    },

    async socketUpdate(msg_data: Record<string, any>, event?: NormalizedEvent) {
      // Placeholder action for $onAction subscribers (edit screens).
      void event;
    },

    async create(
      activityGroupData: ActivityGroupNewData
    ): Promise<ActivityGroup> {
      return await API.createNewActivityGroup(activityGroupData);
    },

    async update(
      group_id: string,
      activityGroup: ActivityGroupUpdate
    ): Promise<ActivityGroup> {
      return await API.update(group_id, activityGroup);
    },

    async delete(activityGroup: ActivityGroup): Promise<ActivityGroup[]> {
      await API.delete(activityGroup);
      return this.getAll();
    },
  },
});
